import type { HistoryPoint } from '../contract';
import { monthLabel } from '../lib/oura';

/**
 * Trend from history. Nulls are gaps, not zeros — a projected/unmeasured month
 * is simply absent from the line, never plotted at the floor (§Your score).
 */
export function TrendChart({ history }: { history: HistoryPoint[] }) {
  const real = history.filter((h) => h.total !== null) as Array<HistoryPoint & { total: number }>;

  if (real.length < 2) {
    return (
      <p className="py-6 text-center text-sm text-gray-400">
        We need one more monthly check before we can draw your trend.
      </p>
    );
  }

  const W = 560;
  const H = 180;
  const pad = { l: 8, r: 8, t: 16, b: 24 };
  const totals = real.map((r) => r.total);
  const min = Math.max(0, Math.min(...totals) - 8);
  const max = Math.min(100, Math.max(...totals) + 8);
  const x = (i: number) => pad.l + (i / (real.length - 1)) * (W - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - (v - min) / (max - min)) * (H - pad.t - pad.b);

  const line = real.map((r, i) => `${x(i)},${y(r.total)}`).join(' ');
  const area = `${line} ${x(real.length - 1)},${H - pad.b} ${x(0)},${H - pad.b}`;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" preserveAspectRatio="none" role="img">
        <polygon points={area} className="fill-blue-500/10" />
        <polyline
          points={line}
          fill="none"
          className="stroke-blue-500"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {real.map((r, i) => (
          <circle key={r.month} cx={x(i)} cy={y(r.total)} r={4} className="fill-blue-500" />
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-xs text-gray-400">
        <span>{monthLabel(real[0].month)}</span>
        <span>{monthLabel(real[real.length - 1].month)}</span>
      </div>
    </div>
  );
}
